import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  post_count: number;
}

interface CategoryFormData {
  name: string;
  slug: string;
}

export default function BlogCategories() {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<BlogCategory | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
  });
  const [formErrors, setFormErrors] = useState<{ name?: string; slug?: string }>({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ categories: BlogCategory[] }>('/admin/blog/categories');
      setCategories(response.categories);
    } catch (err: any) {
      setError(err.message || 'Failed to load categories. Please try again.');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug === '' || prev.slug === generateSlug(prev.name)
        ? generateSlug(value)
        : prev.slug
    }));
    setFormErrors(prev => ({ ...prev, name: undefined }));
  };

  const handleAddClick = () => {
    setEditingCategory(null);
    setFormData({ name: '', slug: '' });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEditClick = (category: BlogCategory) => {
    setEditingCategory(category);
    setFormData({ name: category.name, slug: category.slug });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleDeleteClick = (category: BlogCategory) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: { name?: string; slug?: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Category name is required';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      if (editingCategory) {
        await api.put(`/admin/blog/categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/admin/blog/categories', formData);
      }

      await fetchCategories();
      setModalOpen(false);
      setFormData({ name: '', slug: '' });
      setEditingCategory(null);
    } catch (err) {
      console.error('Error saving category:', err);
      alert('Failed to save category. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/admin/blog/categories/${categoryToDelete.id}`);
      setCategories(categories.filter(c => c.id !== categoryToDelete.id));
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData({ name: '', slug: '' });
    setEditingCategory(null);
    setFormErrors({});
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" role="alert">
        <Card padding="lg">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchCategories} variant="primary">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Categories</h1>
          <p className="text-gray-600 mt-1">Organize your blog posts with categories</p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={handleAddClick}
          aria-label="Add new category"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          Add Category
        </Button>
      </div>

      <Card padding="none">
        {categories.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500 mb-4">No categories found. Create your first category to get started.</p>
            <Button variant="primary" onClick={handleAddClick}>
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              Create Category
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" role="table" aria-label="Blog categories list">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post Count
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {category.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {category.slug}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {category.post_count}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(category)}
                          aria-label={`Edit ${category.name}`}
                        >
                          <Edit2 className="w-4 h-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(category)}
                          disabled={category.post_count > 0}
                          aria-label={`Delete ${category.name}`}
                          title={category.post_count > 0 ? 'Cannot delete category with posts' : 'Delete category'}
                        >
                          <Trash2 className={`w-4 h-4 ${category.post_count > 0 ? 'text-gray-400' : 'text-red-600'}`} aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleModalClose}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Category Name"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={formErrors.name}
            required
            aria-required="true"
            autoFocus
          />

          <Input
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            error={formErrors.slug}
            helperText="URL-friendly version of the name"
            required
            aria-required="true"
          />

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleModalClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={saving}
              disabled={saving}
            >
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        title="Delete Category"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{categoryToDelete?.name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              isLoading={isDeleting}
            >
              Delete Category
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
